import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import {
  FiCalendar,
  FiChevronsRight,
  FiShare2,
  FiDownload,
  FiArrowLeft,
  FiArrowRight,
  FiFileText,
  FiImage,
  FiPaperclip,
} from "react-icons/fi";
import s from "./ActivityDetails.module.css";
import { testUrl } from "../BaseData";

// Sahifa matnlari (uz / en / ru)
const TEXT = {
  uz: {
    home: "Bosh sahifa",
    activity: "Faoliyat",
    related: "Aloqador materiallar",
    viewAll: "Barchasini ko'rish",
    files: "Ilova fayllar",
    download: "Yuklab olish",
    share: "Ulashish",
    copied: "Havola nusxalandi",
    back: "Orqaga",
    loading: "Yuklanmoqda...",
    error: "Ma'lumotlarni yuklashda xatolik",
    notFound: "Maqola topilmadi",
    updated: "Yangilangan",
    fileCount: (n) => `${n} ta fayl`,
  },
  en: {
    home: "Home",
    activity: "Activity",
    related: "Related materials",
    viewAll: "View all",
    files: "Attachments",
    download: "Download",
    share: "Share",
    copied: "Link copied",
    back: "Back",
    loading: "Loading...",
    error: "Failed to load data",
    notFound: "Article not found",
    updated: "Updated",
    fileCount: (n) => `${n} file${n === 1 ? "" : "s"}`,
  },
  ru: {
    home: "Главная",
    activity: "Деятельность",
    related: "Похожие материалы",
    viewAll: "Смотреть все",
    files: "Прикреплённые файлы",
    download: "Скачать",
    share: "Поделиться",
    copied: "Ссылка скопирована",
    back: "Назад",
    loading: "Загрузка...",
    error: "Ошибка загрузки данных",
    notFound: "Статья не найдена",
    updated: "Обновлено",
    fileCount: (n) => `Файлов: ${n}`,
  },
};

// Sidebar'siz ochiladigan alohida sahifalar (Ilmiy kengash, Yoshlar ittifoqi, Xotin-qizlar)
const STANDALONE_IDS = ["199", "196", "195"];

const LOCALES = { uz: "uz-Latn-UZ", en: "en-US", ru: "ru-RU" };

const formatDate = (value, lang) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d)) return "";
  if (lang === "uz") {
    const oy = ["yanvar", "fevral", "mart", "aprel", "may", "iyun", "iyul", "avgust", "sentyabr", "oktyabr", "noyabr", "dekabr"];
    return `${d.getDate()}-${oy[d.getMonth()]}, ${d.getFullYear()}`;
  }
  try {
    return d.toLocaleDateString(LOCALES[lang] || "en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return d.toLocaleDateString();
  }
};

// DB da "https//" (ikki nuqtasiz) saqlangan URLlarni to'g'irlash
const fixSrc = (src) => {
  if (!src) return src;
  src = src.replace(/^(https?)\/\/([^/])/, "$1://$2");
  const doubleMatch = src.match(/https?:\/\/.+?(https?:\/\/.+)$/);
  if (doubleMatch) return doubleMatch[1];
  if (src.startsWith("/")) return testUrl + src;
  return src;
};

const fixHtml = (html) => {
  if (!html) return "";
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    doc.querySelectorAll("img").forEach((img) => {
      img.setAttribute("src", fixSrc(img.getAttribute("src") || ""));
      img.setAttribute("loading", "lazy");
    });
    doc.querySelectorAll("a[href]").forEach((a) => {
      a.setAttribute("href", fixSrc(a.getAttribute("href") || ""));
    });
    return doc.body.innerHTML;
  } catch {
    return html;
  }
};

const imgUrl = (src) => {
  if (!src) return "";
  if (src.startsWith("http")) return src;
  return testUrl + (src.startsWith("/") ? "" : "/") + src;
};

// Fayl kengaytmasi bo'yicha yorliq va ikonka
const fileMeta = (url = "") => {
  const ext = (url.split("?")[0].split(".").pop() || "").toLowerCase();
  const map = {
    pdf: { label: "PDF", tone: "pdf", Icon: FiFileText },
    doc: { label: "DOC", tone: "doc", Icon: FiFileText },
    docx: { label: "DOCX", tone: "doc", Icon: FiFileText },
    xls: { label: "XLS", tone: "xls", Icon: FiFileText },
    xlsx: { label: "XLSX", tone: "xls", Icon: FiFileText },
    ppt: { label: "PPT", tone: "ppt", Icon: FiFileText },
    pptx: { label: "PPTX", tone: "ppt", Icon: FiFileText },
    jpg: { label: "JPG", tone: "img", Icon: FiImage },
    jpeg: { label: "JPEG", tone: "img", Icon: FiImage },
    png: { label: "PNG", tone: "img", Icon: FiImage },
    zip: { label: "ZIP", tone: "zip", Icon: FiPaperclip },
    rar: { label: "RAR", tone: "zip", Icon: FiPaperclip },
  };
  return map[ext] || { label: ext ? ext.toUpperCase() : "FILE", tone: "file", Icon: FiPaperclip };
};

const ActivityDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = TEXT[i18n.language] ? i18n.language : "uz";
  const T = TEXT[lang];

  const [data, setData] = useState(null);
  const [category, setCategory] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    axios
      .get(testUrl + "/api/activity/" + id)
      .then((response) => {
        setData(response.data.activity || null);
        setActivities(response.data.activities || []);
        setCategory(response.data.category || null);
      })
      .catch((err) => setError(err?.response?.status === 404 ? T.notFound : T.error))
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, lang]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2000);
    return () => clearTimeout(t);
  }, [toast]);

  const name = (obj) => (obj ? obj[`name_${lang}`] || obj.name_uz || "" : "");

  const standalone = STANDALONE_IDS.includes((id || "").trim());

  // Faoliyat turi (ActivitiesPost) — kafedra postlarida bo'lmaydi
  const activityType =
    data && data.activity_type && typeof data.activity_type === "object" ? data.activity_type : null;

  // Bo'lim ro'yxati sahifasiga havola (backend category qaytarsa)
  const listPath =
    category?.short_name && activityType?.short_name
      ? `/activity/${category.short_name}/${activityType.short_name}`
      : null;

  const related = useMemo(
    () =>
      (activities || [])
        .filter((a) => a && String(a.id) !== String(id))
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
        .slice(0, 6),
    [activities, id]
  );

  const handleShare = async () => {
    const url = window.location.href;
    const title = name(data);
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
        return;
      }
    } catch {
      /* bekor qilindi */
    }
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setToast(T.copied);
  };

  /* ===== Yuklanish skeleti ===== */
  if (loading) {
    return (
      <div className={s.page}>
        <section className={`${s.hero} ${s.heroSkeleton}`}>
          <div className={s.heroInner}>
            <div className={`${s.skel} ${s.skelCrumb}`} />
            <div className={`${s.skel} ${s.skelTitle}`} />
            <div className={`${s.skel} ${s.skelTitleShort}`} />
            <div className={`${s.skel} ${s.skelMeta}`} />
          </div>
        </section>
        <div className={s.container}>
          <div className={s.layout}>
            <div className={s.article}>
              <div className={`${s.skel} ${s.skelLine}`} />
              <div className={`${s.skel} ${s.skelLine}`} />
              <div className={`${s.skel} ${s.skelLine}`} style={{ width: "70%" }} />
              <div className={`${s.skel} ${s.skelBlock}`} />
              <div className={`${s.skel} ${s.skelLine}`} />
              <div className={`${s.skel} ${s.skelLine}`} style={{ width: "85%" }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={s.page}>
        <div className={s.container}>
          <div className={s.state}>
            <p>{error || T.notFound}</p>
            <button type="button" className={s.btn} onClick={() => navigate(-1)}>
              <FiArrowLeft /> {T.back}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const title = name(data);
  const content = data[`content_${lang}`] || data.content_uz || "";
  const heroImage = imgUrl(data.image);
  const files = Array.isArray(data.files) ? data.files : [];
  const created = formatDate(data.created_at, lang);
  const updated =
    data.updated_at && data.created_at && data.updated_at.slice(0, 10) !== data.created_at.slice(0, 10)
      ? formatDate(data.updated_at, lang)
      : "";

  return (
    <div className={s.page}>
      {/* ===== HERO ===== */}
      <section className={s.hero} style={heroImage ? { backgroundImage: `url(${heroImage})` } : undefined}>
        <div className={s.heroOverlay} />
        <div className={s.heroInner}>
          <nav className={s.breadcrumb}>
            <Link to="/">{T.home}</Link>
            <FiChevronsRight />
            {!standalone && category && (
              <>
                <span className={s.crumbMuted}>{name(category)}</span>
                <FiChevronsRight />
              </>
            )}
            {!standalone && activityType && (
              <>
                {listPath ? (
                  <Link to={listPath}>{name(activityType)}</Link>
                ) : (
                  <span className={s.crumbMuted}>{name(activityType)}</span>
                )}
                <FiChevronsRight />
              </>
            )}
            <span className={s.crumbCurrent}>{title}</span>
          </nav>

          <h1 className={s.heroTitle}>{title}</h1>
          <div className={s.heroLine}>
            <span />
          </div>

          <div className={s.heroMeta}>
            {created && (
              <span className={s.metaItem}>
                <FiCalendar /> {created}
              </span>
            )}
            {activityType && !standalone && (
              <span className={s.metaTag}>{name(activityType)}</span>
            )}
            {files.length > 0 && (
              <span className={s.metaItem}>
                <FiPaperclip /> {T.fileCount(files.length)}
              </span>
            )}
            <button type="button" className={s.shareBtn} onClick={handleShare}>
              <FiShare2 /> {T.share}
            </button>
          </div>
        </div>
      </section>

      {/* ===== BODY ===== */}
      <div className={s.container}>
        <div className={`${s.layout} ${standalone || related.length === 0 ? s.layoutSingle : ""}`}>
          <article className={s.article} data-aos="fade-up">
            <div className={`${s.content} ck-content`} dangerouslySetInnerHTML={{ __html: fixHtml(content) }} />

            {files.length > 0 && (
              <section className={s.filesSection}>
                <h2 className={s.filesTitle}>
                  <FiPaperclip /> {T.files}
                </h2>
                <ul className={s.fileList}>
                  {files.map((file) => {
                    const { label, tone, Icon } = fileMeta(file.file);
                    const fileName = file[`name_${lang}`] || file.name_uz || decodeURIComponent((file.file || "").split("/").pop() || "");
                    return (
                      <li key={file.id} className={s.fileItem}>
                        <a href={fixSrc(file.file)} target="_blank" rel="noopener noreferrer" className={s.fileLink}>
                          <span className={`${s.fileIcon} ${s[`tone_${tone}`] || ""}`}>
                            <Icon />
                            <small>{label}</small>
                          </span>
                          <span className={s.fileName}>{fileName}</span>
                          <span className={s.fileDownload}>
                            <FiDownload /> <span>{T.download}</span>
                          </span>
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}

            {updated && (
              <div className={s.updatedNote}>
                {T.updated}: {updated}
              </div>
            )}
          </article>

          {!standalone && related.length > 0 && (
            <aside className={s.sidebar} data-aos="fade-up" data-aos-delay="100">
              <div className={s.sidebarCard}>
                <h3 className={s.sidebarTitle}>{T.related}</h3>
                <ul className={s.relatedList}>
                  {related.map((item) => (
                    <li key={item.id}>
                      <Link to={`/activity/${item.id}`} className={s.relatedItem}>
                        <span className={s.relatedThumb}>
                          {item.image ? (
                            <img src={imgUrl(item.image)} alt="" loading="lazy" />
                          ) : (
                            <span className={s.relatedThumbEmpty}>
                              <FiImage />
                            </span>
                          )}
                        </span>
                        <span className={s.relatedText}>
                          <span className={s.relatedName}>{name(item)}</span>
                          {item.created_at && (
                            <span className={s.relatedDate}>
                              <FiCalendar /> {formatDate(item.created_at, lang)}
                            </span>
                          )}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
                {listPath && (
                  <Link to={listPath} className={s.viewAll}>
                    {T.viewAll} <FiArrowRight />
                  </Link>
                )}
              </div>
            </aside>
          )}
        </div>
      </div>

      {toast && <div className={s.toast}>{toast}</div>}
    </div>
  );
};

export default ActivityDetail;
